// this needs to stay as absolute path for now due to circular dependencies
import { identifyUser } from '@mote/api/src/requests/tracking/identifyUser'
import { canAccessWebLocalStorage } from '@mote/helpers/assertion'
import {
  ALL_AUTH_PARAMS,
  CANDIDATE_EXTENSIONS,
  GOOGLE_CLIENT_ID,
  MANDATORY_AUTH_PARAMS,
  OAUTH_SCOPES,
  REDIRECT_URI,
  REFERRER_KEY,
  STORAGE_TIMESTAMP,
  WEB_STORAGE_PREFIX_KEYS
} from '@mote/helpers/constants'
import { Diagnostics, DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { getOrMakeAnonymousUserId } from '@mote/helpers/identity/getOrMakeAnonymousUserId'
import window from '@mote/helpers/injectChromeObject'
import { reallyTruthy } from '@mote/helpers/validation'

const logD = new DiagnosticsLogger('WebAuth')

const authArrayData = [
  'productStates',
  'institutionNameHints',
  'goals',
  'assistiveCarousel',
  'affirmativeCarousel'
]

const persistentDataList = [
  'anonymousUserId',
  'anonymousCreatedAt',
  'acquiredScopes',
  'lastUserId'
]

const LOCAL_STORAGE_REFERRER_KEY = 'mote_referrer_user_id'

export function getReferrer() {
  if (!canAccessWebLocalStorage()) return ''

  return window.localStorage.getItem(LOCAL_STORAGE_REFERRER_KEY)
}

export const getDeviceIdOrAccessTokenHash = () => {
  const o = readAllDataInStorage()
  if (o.accessToken) {
    return { accessToken: getAccessToken() }
  } else {
    return { deviceId: getDeviceId() }
  }
}

export const getDeviceId = () => {
  const o = readAllDataInStorage()

  if (!o.anonymousUserId) {
    const { anonymousUserId, anonymousCreatedAt } = getOrMakeAnonymousUserId()
    o.anonymousUserId = anonymousUserId
    o.anonymousCreatedAt = anonymousCreatedAt
  }

  return o.anonymousUserId
}

export function identifyMaybe() {
  const o = readAllDataInStorage()
  if (o.userId && o.accessToken && o.primaryEmailAddress && o.name) {
    identifyUser(o, o.userId)
  }
  return o
}

function _broadcastLoginWithDecoration(o) {
  if (o) {
    const oo = Object.assign({}, o)
    broadcastToExtensions('login', oo)
  }
}

export const identifyMaybeAndBroadcast = () => {
  const o = identifyMaybe()
  _broadcastLoginWithDecoration(o)
}

/**
 * @returns {{} | AuthData}
 */
export function readAllDataInStorage() {
  if (!canAccessWebLocalStorage()) {
    return {}
  }

  const o = {}
  for (const key in ALL_AUTH_PARAMS) {
    const value = window.localStorage.getItem(WEB_STORAGE_PREFIX_KEYS + key)
    try {
      o[key] = JSON.parse(value)
    } catch {
      o[key] = value
    }
  }
  o[REFERRER_KEY] = getReferrer()

  return o
}

export function _persistDataToStorage(o) {
  if (!canAccessWebLocalStorage()) return

  for (const key in ALL_AUTH_PARAMS) {
    if (!['anonymousUserId', 'anonymousCreatedAt'].includes(key)) {
      let value = o[key]
      if (value === undefined || value === null) continue

      if (authArrayData.includes(key)) {
        value = JSON.stringify(value)
      }

      window.localStorage.setItem(WEB_STORAGE_PREFIX_KEYS + key, value)

      if (key === 'userId') {
        window.localStorage.setItem(
          `${WEB_STORAGE_PREFIX_KEYS}lastUserId`,
          value
        )
      }
    }
  }

  window.localStorage.setItem(STORAGE_TIMESTAMP, Date.now() / 1000)
}

export function deleteAllDataInStorage() {
  for (const key in ALL_AUTH_PARAMS) {
    if (!persistentDataList.includes(key)) {
      window.localStorage.removeItem(WEB_STORAGE_PREFIX_KEYS + key)
    }
  }
}

export function _persistBroadcastIdentify(data, skipIdentify = false) {
  _persistDataToStorage(data)
  _broadcastLoginWithDecoration(data)

  if (skipIdentify) return

  identifyUser(data, data.userId)
}

export function responseIncludesAuth(o) {
  for (const key in MANDATORY_AUTH_PARAMS) {
    if (!o[key]) {
      return false
    }
  }
  return true
}

export const probe = cb => {
  broadcastToExtensions('probe', {}, cb)
}

export const broadcastPreferredLocale = selectedLanguage => {
  broadcastToExtensions('preferred_locale', {
    preferredLocale: selectedLanguage
  })
}

export const broadcastLastLoginHostname = () => {
  broadcastToExtensions('last_login_hostname', {
    lastLoginHostname: window.location.hostname
  })
}

export const broadcastExternalWebsiteRedirection = redirectionInfo => {
  broadcastToExtensions('redirect_to', redirectionInfo)
}

export async function broadcastToExtensions(event, o, callback) {
  if (!window.chrome?.runtime) return

  for (let extensionId of CANDIDATE_EXTENSIONS) {
    try {
      const sendData = { ...o, extensionId: extensionId }
      await window.chrome.runtime.sendMessage(
        extensionId,
        { type: event, data: sendData },
        response => {
          if (window.chrome.runtime.lastError) {
            return
          }
          if (callback) {
            callback(response)
          }
        }
      )
    } catch (error) {
      logD.error(
        `Could not send a message to the the extension with ID=${extensionId}`,
        {
          error
        }
      )
      Diagnostics.dispatch({
        dispatchContext: 'EXTENSION_BROADCAST',
        title: "Failed to broadcast to extension's background script"
      })
      console.error(
        'Could not send a message to the the extension with ID=',
        extensionId,
        error
      )
    }
  }
}

export const getMoteLogin = readAllDataInStorage

export const getAccessToken = () => {
  const o = readAllDataInStorage()
  if (o.accessToken) {
    return o.accessToken
  } else {
    return ''
  }
}

export const getUserId = () => {
  const o = readAllDataInStorage()
  if (o.userId) {
    return o.userId
  } else {
    return ''
  }
}

export const isPaid = () => {
  const o = readAllDataInStorage()
  try {
    if (o.productStates && o.productStates[0].isPaid) {
      return o.productStates[0].isPaid
    }
  } catch (e) {}
  return false
}

export const getUsersGivenName = () => {
  const o = readAllDataInStorage()
  if (o.givenName) {
    return o.givenName
  } else {
    return ''
  }
}

export const getUsersFamilyName = () => {
  const o = readAllDataInStorage()
  if (o.familyName) {
    return o.familyName
  } else {
    return ''
  }
}

export const getUsersEmail = () => {
  const o = readAllDataInStorage()
  if (o.primaryEmailAddress) {
    return o.primaryEmailAddress
  } else {
    return ''
  }
}

export const isMinor = () => {
  const o = readAllDataInStorage()
  return reallyTruthy(o.isMinor)
}

export const isInstitutional = () => {
  const o = readAllDataInStorage()
  return reallyTruthy(o.isInstitutional)
}

export const isInstitutionalAdmin = () => {
  const o = readAllDataInStorage()
  return reallyTruthy(o.isInstitutionalAdmin)
}

/**
 * @param state
 * @param {OAuthPath} path
 * @returns string
 */
export const getOAuthURL = state => {
  const baseUrl = 'https://accounts.google.com/o/oauth2/auth?'
  const params = {
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: OAUTH_SCOPES.join(' '),
    access_type: 'offline',
    prompt: 'consent',
    state: new URLSearchParams(state)?.toString()
  }
  return `${baseUrl}${new URLSearchParams(params)?.toString()}`
}

export const getReferrerCodeURL = alternativeReferrerCode => {
  const o = readAllDataInStorage()
  const rc = o.referrerCode || alternativeReferrerCode
  if (rc && rc.length > 0 && rc.length < 10) {
    return window.location.protocol + '//' + window.location.host + '/r/' + rc
  } else {
    console.error('no referrer code: ', alternativeReferrerCode)
    return ''
  }
}
